import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  AuthResponse,
  DashboardData,
  BackendResponse,
  Client,
  ClientRequest,
  Supplier,
  SupplierRequest,
  Product,
  Category,
  Order,
  OrderItem,
  Payment,
  AddPaymentRequest,
  Invoice,
  InvoiceRequest,
  PaymentMethod,
  CompanyRequest,
  Company,
  Agent, CreateAgentRequest, InvoiceSearchQuery, InvoiceSearchResponse
} from '../types';
import { encryptSensitiveFields, decryptSensitiveFields } from '../utils/EncryptionUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token to headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on 401 for protected routes (not auth routes)
    if (error.response?.status === 401 && !error.config.url?.includes('/auth/')) {
      // Token is invalid or expired for protected routes
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/login', { email, password });
    return response.data;
  },

  signup: async (email: string, login: string, password: string): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/signup', { email, login, password });
    return response.data;
  },

  forgotPassword: async (email: string): Promise<BackendResponse> => {
    const response: AxiosResponse<BackendResponse> = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (resetToken: string, newPassword: string): Promise<BackendResponse> => {
    const response: AxiosResponse<BackendResponse> = await api.post('/auth/reset-password', { resetToken, newPassword });
    return response.data;
  },

  verifyEmail: async (verificationToken: string): Promise<{ message: string; email?: string }> => {
    const response: AxiosResponse<{ message: string; email?: string }> = await api.post('/auth/verify-email', { verificationToken });
    return response.data;
  },

  resendVerification: async (email: string): Promise<BackendResponse> => {
    const response: AxiosResponse<BackendResponse> = await api.post('/auth/resend-verification', { email });
    return response.data;
  }
};

export const profileAPI = {
  changePassword: async (currentPassword: string, newPassword: string): Promise<BackendResponse> => {
    const response: AxiosResponse<BackendResponse> = await api.post('/profile/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  },

  getProfileInfo: async (): Promise<any> => {
    const response: AxiosResponse<any> = await api.get('/profile/info');
    return response.data;
  },
};

export const clientAPI = {
  SENSITIVE_FIELDS: new Set([
    'ice',
    'name',
    'number',
    'street',
    'city',
    'postalCode',
    'country',
    'phone',
    'email',
    'website'
  ]),

  createClient: async (clientData: ClientRequest): Promise<Client> => {
    const encryptedData = encryptSensitiveFields(clientAPI.SENSITIVE_FIELDS, clientData);
    const response: AxiosResponse<Client> = await api.post('/clients', encryptedData);
    return decryptSensitiveFields(clientAPI.SENSITIVE_FIELDS, response.data) as Client;
  },

  getClientsByCompany: async (companyId: string): Promise<Client[]> => {
    const response: AxiosResponse<Client[]> = await api.get(`/clients?companyId=${companyId}`);
    return decryptSensitiveFields(clientAPI.SENSITIVE_FIELDS, response.data) as Client[];
  },

  getClientById: async (clientId: string): Promise<Client> => {
    const response: AxiosResponse<Client> = await api.get(`/clients/${clientId}`);
    return decryptSensitiveFields(clientAPI.SENSITIVE_FIELDS, response.data) as Client;
  },

  updateClient: async (clientId: string, clientData: ClientRequest): Promise<Client> => {
    const encryptedData = encryptSensitiveFields(clientAPI.SENSITIVE_FIELDS, clientData);
    const response: AxiosResponse<Client> = await api.put(`/clients/${clientId}`, encryptedData);
    return decryptSensitiveFields(clientAPI.SENSITIVE_FIELDS, response.data) as Client;
  },

  deleteClient: async (clientId: string): Promise<void> => {
    await api.delete(`/clients/${clientId}`);
  },

  searchClientsByName: async (companyId: string, name: string): Promise<Client[]> => {
    const response: AxiosResponse<Client[]> = await api.get(`/clients/search/name?companyId=${companyId}&name=${encodeURIComponent(name)}`);
    return decryptSensitiveFields(clientAPI.SENSITIVE_FIELDS, response.data) as Client[];
  },

  searchClientsByEmail: async (companyId: string, email: string): Promise<Client[]> => {
    const response: AxiosResponse<Client[]> = await api.get(`/clients/search/email?companyId=${companyId}&email=${encodeURIComponent(email)}`);
    return decryptSensitiveFields(clientAPI.SENSITIVE_FIELDS, response.data) as Client[];
  },

  searchClientsByPhone: async (companyId: string, phone: string): Promise<Client[]> => {
    const response: AxiosResponse<Client[]> = await api.get(`/clients/search/phone?companyId=${companyId}&phone=${encodeURIComponent(phone)}`);
    return decryptSensitiveFields(clientAPI.SENSITIVE_FIELDS, response.data) as Client[];
  },

  getNextClientNumber: async (companyId: string): Promise<number> => {
    const response: AxiosResponse<number> = await api.get(`/clients/next-number?companyId=${companyId}`);
    return response.data;
  },

  searchClientsAdvanced: async (
    request: {
      companyId: string;
      clientNumber?: number;
      name?: string;
      email?: string;
      page: number;
      size: number;
    }
  ): Promise<{
    content: Client[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    last: boolean;
  }> => {
    const response: AxiosResponse<{
      content: Client[];
      totalElements: number;
      totalPages: number;
      size: number;
      number: number;
      last: boolean;
    }> = await api.post(`/clients/search`, request);

    return {
      ...response.data,
      content: decryptSensitiveFields(clientAPI.SENSITIVE_FIELDS, response.data.content) as Client[],
    };
  },
};

export const agentAPI = {

  getAllAgents: async (): Promise<Agent[]> => {
    const response: AxiosResponse<Agent[]> = await api.get('/agents');
    return response.data;
  } ,

  deleteAgent: async (agentId: string): Promise<void> => {
    await api.delete(`/agents/${agentId}`);
  },

  createAgent: async (agentRequest: CreateAgentRequest): Promise<void> => {
    const response: AxiosResponse<void> = await api.post(`/agents`, agentRequest);
    return response.data;
  },

  updateAgent: async (agentId: string, agentRequest: CreateAgentRequest): Promise<void> => {
    const response: AxiosResponse<void> = await api.put(`/agents/${agentId}`, agentRequest);
    return response.data;
  }
}

export const companyAPI = {
  createCompany: async (companyData: CompanyRequest): Promise<Company> => {
    const formData = new FormData();
    Object.entries(companyData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value as string | Blob);
      }
    });

    const response: AxiosResponse<Company> = await api.post('/companies', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getAllCompanies: async (): Promise<Company[]> => {
    const response: AxiosResponse<Company[]> = await api.get('/companies');
    return response.data;
  },

  getCompanyById: async (companyId: string, withLogo = false): Promise<Company> => {
    const response: AxiosResponse<Company> = await api.get(`/companies/${companyId}?withLogo=${withLogo}`);
    return response.data;
  },

  updateCompany: async (companyId: string, companyData: CompanyRequest): Promise<Company> => {
    const formData = new FormData();
    Object.entries(companyData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value as string | Blob);
      }
    });

    const response: AxiosResponse<Company> = await api.put(`/companies/${companyId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteCompany: async (companyId: string): Promise<void> => {
    await api.delete(`/companies/${companyId}`);
  },
};

// Payment API
export const addPaymentToInvoice = async (invoiceId: string, payment: AddPaymentRequest): Promise<Order> => {
  const response = await api.post(`/orders/invoice/${invoiceId}/payment`, payment);
  return response.data;
};

export const updatePayment = async (orderId: string, paymentId: string, payment: Payment): Promise<Order> => {
  const response = await api.put(`/orders/${orderId}/payments/${paymentId}`, payment);
  return response.data;
};

export const deletePayment = async (orderId: string, paymentId: string): Promise<Order> => {
  const response = await api.delete(`/orders/${orderId}/payments/${paymentId}`);
  return response.data;
};

export const addPayments = async (orderId: string, payments: Payment[]): Promise<Order> => {
  const response = await api.post(`/orders/${orderId}/payments/batch`, payments);
  return response.data;
};

export const getOrderByInvoiceId = async (invoiceId: string): Promise<Order> => {
  const response = await api.get<Order>(`/orders/invoice/${invoiceId}`);
  return response.data;
};

export const invoiceAPI = {
  // Sensitive client fields embedded in invoices (prefixed with 'client')
  INVOICE_CLIENT_SENSITIVE_FIELDS: new Set([
    'clientIce',
    'clientName',
    'clientStreet',
    'clientCity',
    'clientPostalCode',
    'clientCountry'
  ]),

  // Helper to decrypt invoice client fields
  decryptInvoiceClientFields: (invoice: Invoice): Invoice => {
    return decryptSensitiveFields(invoiceAPI.INVOICE_CLIENT_SENSITIVE_FIELDS, invoice) as Invoice;
  },

  // Helper to decrypt array of invoices
  decryptInvoicesClientFields: (invoices: Invoice[]): Invoice[] => {
    return invoices.map(invoice => invoiceAPI.decryptInvoiceClientFields(invoice));
  },

  createInvoice: async (invoiceRequest: InvoiceRequest): Promise<Invoice> => {
    const response: AxiosResponse<Invoice> = await api.post('/invoices', invoiceRequest);
    return invoiceAPI.decryptInvoiceClientFields(response.data);
  },

  getInvoiceById: async (invoiceId: string): Promise<Invoice> => {
    const response: AxiosResponse<Invoice> = await api.get(`/invoices/${invoiceId}`);
    return invoiceAPI.decryptInvoiceClientFields(response.data);
  },

  searchInvoicesAdvanced: async (
    request: InvoiceSearchQuery
  ): Promise<InvoiceSearchResponse> => {
    const response: AxiosResponse<{
      content: Invoice[];
      totalElements: number;
      totalPages: number;
      size: number;
      number: number;
      last: boolean;
    }> = await api.post(`/invoices/search`, request);

    return {
      ...response.data,
      content: invoiceAPI.decryptInvoicesClientFields(response.data.content),
      last: response.data.last
    };
  },

  updateInvoice: async (invoiceId: string, invoiceRequest: InvoiceRequest): Promise<Invoice> => {
    const response: AxiosResponse<Invoice> = await api.put(`/invoices/${invoiceId}`, invoiceRequest);
    return invoiceAPI.decryptInvoiceClientFields(response.data);
  },

  linkQuoteToInvoice: async (newInvoiceId: string, originalQuoteId: string | undefined): Promise<void> => {
    await api.post(`/invoices/quoteToInvoiceLink?newInvoiceId=${newInvoiceId}&originalQuoteId=${originalQuoteId}`, {});
  },

  deleteInvoice: async (invoiceId: string): Promise<void> => {
    await api.delete(`/invoices/${invoiceId}`);
  },

  getNextInvoiceNumberForYearAndType: async (companyId: string, year: number, invoiceType: string): Promise<number> => {
    const response: AxiosResponse<number> = await api.get(`/invoices/next-number/${companyId}/${year}/${encodeURIComponent(invoiceType)}`);
    return response.data;
  },

  getCashTotals: async (companyId: string): Promise<{ dailyTotal: number; monthlyTotal: number }> => {
    const response: AxiosResponse<{ dailyTotal: number; monthlyTotal: number }> = await api.get(`/invoices/cash-totals/${companyId}`);
    return response.data;
  },

  generateLink: async (invoiceId: string, publicData: any): Promise<Invoice> => {
    const response: AxiosResponse<Invoice> = await api.post(`/invoices/${invoiceId}/generate-link`, publicData);
    return invoiceAPI.decryptInvoiceClientFields(response.data);
  },

  previewPDF: (generationId: string): string => {
    return `${API_BASE_URL}/public/view-invoice/${generationId}`;
  },

};

export const templateAPI = {
  createTemplate: async (template: any): Promise<any> => {
    const response: AxiosResponse<any> = await api.post('/templates', template);
    return response.data;
  },

  getTemplates: async (): Promise<any[]> => {
    const response: AxiosResponse<any[]> = await api.get(`/templates`);
    return response.data;
  },

  updateTemplate: async (templateId: string, template: any): Promise<any> => {
    const response: AxiosResponse<any> = await api.put(`/templates/${templateId}`, template);
    return response.data;
  },

  deleteTemplate: async (templateId: string): Promise<void> => {
    await api.delete(`/templates/${templateId}`);
  },

  generatePDFBytes: async (templateId: string | undefined, invoiceData?: any): Promise<Blob> => {
    const response: AxiosResponse<Blob> = await api.post(`/templates/generate-pdf-bytes${templateId ? '?id='+templateId : ''}`, invoiceData, {
      responseType: 'blob'
    });

    return new Blob([response.data], { type: 'application/pdf' });
  },

  previewPDFBytes: async (templateId: string): Promise<Blob> => {
    const response: AxiosResponse<Blob> = await api.post(`/templates/preview-pdf-bytes${templateId ? '?id='+templateId : ''}`, {}, {
      responseType: 'blob'
    });

    return new Blob([response.data], { type: 'application/pdf' });
  },

  generatePDFBytesAndOpen: async (templateId: string, invoiceData?: any): Promise<void> => {
    const blob = await templateAPI.generatePDFBytes(templateId, invoiceData);

    // Create a download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice_${templateId}_${new Date().toISOString().split('T')[0]}.pdf`;
    link.style.display = 'none';

    // Append to DOM, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL after a delay
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
  },

  openPDFInNewTab: async (templateId: string | undefined, invoiceData?: any): Promise<void> => {
    const blob = await templateAPI.generatePDFBytes(templateId, invoiceData);

    // Create blob URL and open in new tab
    const url = window.URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');

    // Clean up the blob URL after the window is closed or after a delay
    if (newWindow) {
      newWindow.onbeforeunload = () => {
        window.URL.revokeObjectURL(url);
      };
    } else {
      // Fallback cleanup if window.open fails
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    }
  },

  cleanTemplate: async (htmlTemplate: string): Promise<string> => {
    const response: AxiosResponse<{ cleanedHtml: string }> = await api.post('/templates/clean', { htmlTemplate });
    return response.data.cleanedHtml;
  },

  getStaticTemplateOptions: async (): Promise<string[]> => {
    const response: AxiosResponse<string[]> = await api.get('/templates/static-options');
    return response.data;
  },

  upsertStaticTemplate: async (templateName: string, companyId: string | undefined | null): Promise<any> => {
    const response: AxiosResponse<any> = await api.put('/templates/static', {
      templateName: templateName,
      companyId: companyId
    });
    return response.data;
  },

  selectTemplate: async (templateId: string, companyId: string | undefined | null): Promise<void> => {
    await api.put(`/templates/${templateId}/select`, { companyId });
  },
};

export default api;
